import React, { useState, useEffect, memo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { values } from "lodash";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import Tooltip from "@mui/material/Tooltip";
import { MemberNoValidation, MenuProps, DropdownSx } from "../../../utils";
import useStableCallback from "../../../hooks/useStableCallback";
import {
  setDashboardFilters,
  resetDashboardFilters,
} from "../../../store/globalSlice";

const TitleSx = { fontWeight: "bold", fontSize: 16.5 };

const ActionSx = { display: "flex", justifyContent: "flex-end" };

const ResetBtnSx = { mr: 3 };

const AdvanceSearch = (props) => {
  const dispatch = useDispatch();
  const {
    memberNumber,
    storeName,
    city,
    state,
    statusCode,
    projectTypeId,
    planner,
    mac,
    startDate,
    endDate,
  } = useSelector((store) => store.global.dashboardFilters);
  const [searchExpand, setSearchExpand] = useState(() => {
    return !values({
      memberNumber,
      storeName,
      city,
      state,
      statusCode,
      projectTypeId,
      planner,
      mac,
      startDate,
      endDate,
    }).every((i) => !i);
  });
  const [memberNumberError, setMemberNumberError] = useState("");
  const [sdErr, setSdErr] = useState("");
  const [edErr, setEdErr] = useState("");

  useEffect(() => {
    if (!!memberNumber) {
      isMemberNumberValid(memberNumber);
    }
  }, []);

  useEffect(() => {
    let today = new Date();
    today = `${today.getFullYear()}-${`0${today.getMonth() + 1}`.slice(
      -2
    )}-${`0${today.getDate()}`.slice(-2)}`;

    if (startDate && endDate === "") {
      setEdErr("Please select End Date");
      setSdErr("");
    } else if (endDate && startDate === "") {
      setSdErr("Please select Start Date");
      setEdErr("");
    } else if (endDate < startDate) {
      setEdErr("End Date should not be less than Start Date");
      setSdErr("");
    } else if (startDate > today && endDate > today) {
      setSdErr("Start Date should not be greater than today's date");
      setEdErr("End Date should not be greater than today's date");
    } else if (startDate > today) {
      setSdErr("Start Date should not be greater than today's date");
      setEdErr("");
    } else if (endDate > today) {
      setEdErr("End Date should not be greater than today's date");
      setSdErr("");
    } else {
      setSdErr("");
      setEdErr("");
    }
  }, [startDate, endDate]);

  const isMemberNumberValid = useStableCallback((value) => {
    const regex = MemberNoValidation.pattern.value;
    const message = MemberNoValidation.pattern.message;
    setMemberNumberError(
      value.length >= 1 && !regex.test(value) ? message : ""
    );
  });

  const handleMemberNumberChange = useStableCallback((e) => {
    const newValue = e?.target?.value;
    isMemberNumberValid(newValue);
    dispatch(setDashboardFilters({ field: "memberNumber", value: newValue }));
  });

  const handleStoreNameChange = useStableCallback((e) => {
    dispatch(
      setDashboardFilters({ field: "storeName", value: e?.target?.value })
    );
  });

  const handleCityChange = useStableCallback((e) => {
    dispatch(setDashboardFilters({ field: "city", value: e?.target?.value }));
  });

  const handleStateChange = useStableCallback((e) => {
    dispatch(setDashboardFilters({ field: "state", value: e?.target?.value }));
  });

  const handleStatusChange = useStableCallback((e) => {
    dispatch(
      setDashboardFilters({ field: "statusCode", value: e?.target?.value })
    );
  });

  const handleProjectTypeChange = useStableCallback((e) => {
    dispatch(
      setDashboardFilters({ field: "projectTypeId", value: e?.target?.value })
    );
  });

  const handlePlannerChange = useStableCallback((e) => {
    dispatch(
      setDashboardFilters({ field: "planner", value: e?.target?.value })
    );
  });

  const handleMacChange = useStableCallback((e) => {
    dispatch(setDashboardFilters({ field: "mac", value: e?.target?.value }));
  });

  const handleStartDateChange = useStableCallback((e) => {
    dispatch(
      setDashboardFilters({ field: "startDate", value: e?.target?.value })
    );
  });

  const handleEndDateChange = useStableCallback((e) => {
    dispatch(
      setDashboardFilters({ field: "endDate", value: e?.target?.value })
    );
  });

  const handleAdvanceSearch = useStableCallback(() => {
    if (!memberNumberError && !sdErr && !edErr) {
      props.handleSearch();
    }
  });

  const handleReset = useStableCallback(() => {
    dispatch(resetDashboardFilters());
    setMemberNumberError("");
    setSdErr("");
    setEdErr("");
  });

  const handleExpand = useStableCallback(() => {
    setSearchExpand(!searchExpand);
  });

  return (
    <form noValidate autoComplete="off">
      <Accordion expanded={searchExpand} onChange={handleExpand}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Typography sx={TitleSx}>Advance Search</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4} md={3} lg={3}>
              <Tooltip title="Enter Member Number" arrow>
                <TextField
                  fullWidth
                  margin="dense"
                  size="small"
                  label="Member Number"
                  InputLabelProps={{ shrink: !!memberNumber }}
                  inputProps={{ maxLength: 7 }}
                  value={memberNumber}
                  onChange={handleMemberNumberChange}
                  error={!!memberNumberError}
                  helperText={memberNumberError}
                />
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={3}>
              <Tooltip title="Enter Store Name (Max 100 characters)" arrow>
                <TextField
                  fullWidth
                  margin="dense"
                  size="small"
                  label="Store Name"
                  inputProps={{ maxLength: 100 }}
                  InputLabelProps={{ shrink: !!storeName }}
                  value={storeName}
                  onChange={handleStoreNameChange}
                />
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={3}>
              <Tooltip title="Enter Store's City (Max 100 characters)" arrow>
                <TextField
                  fullWidth
                  margin="dense"
                  size="small"
                  label="City"
                  inputProps={{ maxLength: 100 }}
                  InputLabelProps={{ shrink: !!city }}
                  value={city}
                  onChange={handleCityChange}
                />
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={3}>
              <Tooltip title="Enter Store's State (Max 100 characters)" arrow>
                <TextField
                  fullWidth
                  margin="dense"
                  size="small"
                  label="State"
                  inputProps={{ maxLength: 100 }}
                  InputLabelProps={{ shrink: !!state }}
                  value={state}
                  onChange={handleStateChange}
                />
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={3}>
              <FormControl size="small" fullWidth margin="dense">
                <InputLabel id="status">Project Status</InputLabel>
                <Select
                  labelId="status"
                  label="Project Status"
                  value={statusCode}
                  onChange={handleStatusChange}
                  MenuProps={MenuProps}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem key={0} value={0}>
                    All
                  </MenuItem>
                  {props.psData?.map((name) => (
                    <MenuItem
                      sx={DropdownSx}
                      key={name.ConfigKey}
                      value={name.ConfigKey}
                    >
                      {name.ConfigValue}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={3}>
              <FormControl size="small" fullWidth margin="dense">
                <InputLabel id="projType">Project Type</InputLabel>
                <Select
                  labelId="projType"
                  label="Project Type"
                  value={projectTypeId}
                  onChange={handleProjectTypeChange}
                  MenuProps={MenuProps}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem key={0} value={0}>
                    All
                  </MenuItem>
                  {props.ptData?.map((name) => (
                    <MenuItem
                      sx={DropdownSx}
                      key={name.ConfigKey}
                      value={name.ConfigKey}
                    >
                      {name.ConfigValue}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={3}>
              <FormControl size="small" fullWidth margin="dense">
                <InputLabel id="planner">Project Planner</InputLabel>
                <Select
                  labelId="planner"
                  label="Project Planner"
                  value={planner}
                  onChange={handlePlannerChange}
                  MenuProps={MenuProps}
                >
                  <MenuItem value="">None</MenuItem>
                  {props.plannerData?.map((name) => (
                    <MenuItem
                      sx={DropdownSx}
                      key={name.UserId}
                      value={name.UserId}
                    >
                      {name.Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={3}>
              <FormControl size="small" fullWidth margin="dense">
                <InputLabel id="mac">Assigned MAC</InputLabel>
                <Select
                  labelId="mac"
                  label="Assigned MAC"
                  value={mac}
                  onChange={handleMacChange}
                  MenuProps={MenuProps}
                >
                  <MenuItem value="">None</MenuItem>
                  {props.macData?.map((name) => (
                    <MenuItem
                      sx={DropdownSx}
                      key={name.UserId}
                      value={name.UserId}
                    >
                      {name.Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={3}>
              <TextField
                fullWidth
                margin="dense"
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                helperText={sdErr}
                error={sdErr}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={3}>
              <TextField
                fullWidth
                margin="dense"
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                helperText={edErr}
                error={edErr}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} sx={ActionSx}>
              <Button variant="outlined" onClick={handleReset} sx={ResetBtnSx}>
                Reset
              </Button>
              <Button variant="contained" onClick={handleAdvanceSearch}>
                Search
              </Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </form>
  );
};

export default memo(AdvanceSearch);
